import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerFinancialAccountsRoute } from "network/api-routes";
import { revaAdminSetAccountBillingUrl, revaAdminSetAccountCobrosUrl } from "utils/revaAdminBillingUrl";
import { formatSubscriptionStatus } from "utils/formatSubscriptionStatus";
import { formatFinancialAmount } from "./financial-helpers";

const accountLabel = (account) => {
  if (!account) return "";
  const name = account.name || `#${account.id}`;
  return account.currency_code ? `${name} (${account.currency_code})` : name;
};

const cobrosSelectedId = (data) => {
  const accountId = data?.gestion?.account_id;
  const billingId = data?.billing?.account_id;
  if (accountId == null) return "";
  if (billingId != null && String(accountId) === String(billingId)) return "";
  return String(accountId);
};

const GestionFinancialAccountsCard = ({ customerId, user, onUpdated, section = "all", embedded = false, compact = false }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payload, setPayload] = useState(null);
  const [selectedId, setSelectedId] = useState("");

  const load = () => {
    if (!customerId || !user) return;
    setLoading(true);
    performRequest("GET", leagueCustomerFinancialAccountsRoute(customerId), {}, true, user)
      .then((response) => {
        const data = response.data;
        setPayload(data);
        setSelectedId(cobrosSelectedId(data));
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, user]);

  const handleSave = () => {
    const billingAccountId = payload?.billing?.account_id;
    if (!selectedId || (billingAccountId != null && String(selectedId) === String(billingAccountId))) {
      return;
    }
    setSaving(true);
    performRequest(
      "PATCH",
      leagueCustomerFinancialAccountsRoute(customerId),
      { gestion_account_id: Number(selectedId) },
      true,
      user,
    )
      .then((response) => {
        setPayload(response.data);
        setSelectedId(cobrosSelectedId(response.data));
        enqueueSnackbar(intl.formatMessage({ id: "gestion.financials.accounts.saved" }), { variant: "success" });
        onUpdated?.();
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        });
      })
      .finally(() => setSaving(false));
  };

  if (loading && !payload) {
    return null;
  }

  const billing = payload?.billing;
  const gestion = payload?.gestion;
  const billingId = billing?.account_id != null ? String(billing.account_id) : "";
  const candidates = (payload?.candidates || []).filter((account) => String(account.id) !== billingId);
  const changeLocked = Boolean(gestion?.change_locked);
  const billingSelected = Boolean(gestion?.account_id) && String(gestion.account_id) === billingId;
  const gestionAccount = billingSelected ? null : gestion?.account;
  const selectValue = selectedId && selectedId !== billingId ? selectedId : "";
  const selectedCandidate = candidates.find((account) => String(account.id) === selectValue);
  const displayedAccount = gestionAccount || selectedCandidate || null;
  const displayedIsSelectedCobros =
    Boolean(displayedAccount) && String(displayedAccount.id) !== billingId;
  const persistedGestionAccountId = gestion?.account_id;
  const canConfigureCobros =
    persistedGestionAccountId != null && String(persistedGestionAccountId) !== billingId;
  const gestionCurrency = displayedAccount?.currency_code || "";
  const gestionBalance =
    Number(displayedAccount?.available_balance ?? 0) + Number(displayedAccount?.pending_balance ?? 0);
  const showBilling = section === "all" || section === "billing";
  const showGestion = section === "all" || section === "gestion";

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      sx={{
        mb: embedded ? 0 : 3,
        flex: embedded && !compact ? 1 : undefined,
        width: compact ? "100%" : undefined,
        minWidth: 0,
      }}
      alignItems="stretch"
    >
      {showBilling && (
        <Card
          variant="outlined"
          sx={embedded ? { width: "100%", flex: 1 } : { width: { xs: "100%", sm: 320 }, flexShrink: 0 }}
        >
          <CardContent>
            <RevaText variant="overline" color="text.secondary">
              {intl.formatMessage({ id: "gestion.financials.accounts.billingTitle" })}
            </RevaText>
            {billing ? (
              <>
                <RevaText variant="h5" sx={{ mt: 1 }}>
                  {formatFinancialAmount(billing.available_balance, billing.currency_code)}
                </RevaText>
                <RevaText variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {formatSubscriptionStatus(intl, billing.subscription_status)}
                </RevaText>
              </>
            ) : (
              <RevaText variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {intl.formatMessage({ id: "gestion.financials.accounts.billingMissing" })}
              </RevaText>
            )}
          </CardContent>
          {billing?.account_id && (
            <CardActions>
              <Button
                size="small"
                href={revaAdminSetAccountBillingUrl(billing.account_id)}
                component="a"
                target="_blank"
                rel="noopener noreferrer"
              >
                {intl.formatMessage({ id: "gestion.financials.accounts.viewAccount" })}
              </Button>
            </CardActions>
          )}
        </Card>
      )}

      {showGestion && (
        <Card variant="outlined" sx={{ flex: compact ? undefined : 1, width: compact ? "100%" : undefined, minWidth: 0 }}>
        <CardContent>
          <RevaText variant="overline" color="text.secondary">
            {intl.formatMessage({ id: "gestion.financials.accounts.gestionTitle" })}
          </RevaText>
          {displayedIsSelectedCobros ? (
            <>
              <RevaText variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                {accountLabel(displayedAccount)}
              </RevaText>
              <RevaText variant="h5" sx={{ mt: 0.5 }}>
                {formatFinancialAmount(gestionBalance, gestionCurrency)}
              </RevaText>
            </>
          ) : (
            <RevaText variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {intl.formatMessage({ id: "gestion.financials.accounts.gestionNone" })}
            </RevaText>
          )}

          {billingSelected && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: "gestion.financials.accounts.billingNotAllowed" })}
            </Alert>
          )}

          {changeLocked ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {intl.formatMessage({ id: "gestion.financials.accounts.locked" })}
            </Alert>
          ) : (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel id="gestion-account-select-label" shrink>
                  {intl.formatMessage({ id: "gestion.financials.accounts.gestionSelect" })}
                </InputLabel>
                <Select
                  labelId="gestion-account-select-label"
                  label={intl.formatMessage({ id: "gestion.financials.accounts.gestionSelect" })}
                  value={selectValue}
                  displayEmpty
                  onChange={(event) => setSelectedId(event.target.value)}
                  renderValue={(value) => {
                    if (!value) {
                      return intl.formatMessage({ id: "gestion.financials.accounts.selectPlaceholder" });
                    }
                    const account = candidates.find((item) => String(item.id) === String(value));
                    return account ? accountLabel(account) : value;
                  }}
                >
                  <MenuItem value="" disabled>
                    {intl.formatMessage({ id: "gestion.financials.accounts.selectPlaceholder" })}
                  </MenuItem>
                  {candidates.map((account) => (
                    <MenuItem key={account.id} value={String(account.id)}>
                      {accountLabel(account)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {candidates.length === 0 && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {intl.formatMessage({ id: "gestion.financials.accounts.noCandidates" })}
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !selectValue || selectValue === billingId}
              >
                {intl.formatMessage({ id: "gestion.financials.accounts.save" })}
              </Button>
            </Box>
          )}
        </CardContent>
        <CardActions>
          {canConfigureCobros ? (
            <Button
              size="small"
              href={revaAdminSetAccountCobrosUrl(gestion.account_id)}
              component="a"
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage({ id: "gestion.financials.accounts.configureCobros" })}
            </Button>
          ) : (
            <Button size="small" disabled>
              {intl.formatMessage({ id: "gestion.financials.accounts.configureCobros" })}
            </Button>
          )}
        </CardActions>
        </Card>
      )}
    </Stack>
  );
};

export default GestionFinancialAccountsCard;
